import { IBuildingProviderContext } from '../@types/ContextProviderRelated';
import { PvSceneExport } from '../importer/structures/panoviewer/new/@interfaces/@v_1_0/SceneExport';
import { getValidKeycloakAccessTokenOrFallback } from '../keycloak';
import PlanModel from '../models/standard/PlanModel';
import apiService from './apiService';
import { API_URL, APP_VERSION, JSON_VERSION } from './constants';
import { ERegionCode } from './regionUtils';

export const decryptPlanData = async (data: string, building: IBuildingProviderContext, authToken?: string): Promise<{ plan: PlanModel }> => {
  const result = await apiService.postRequest('decrypt-data', { data }, authToken);
  const plan = new PlanModel(
    result.description ?? '',
    result.floorplanId,
    result.kojeId,
    result.kojeLevel,
    result.eventId,
    result.dataObjectId,
    result.showroomId,
    APP_VERSION,
    building,
    JSON_VERSION,
    result.region,
  );
  return { plan };
};

export const exchangeToken = async (email: string, token: string): Promise<string> => {
  try {
    const authResponse = await apiService.postRequest('exchangetoken', { email, token });

    if (!authResponse || authResponse.status !== 'ACCESS GRANTED') {
      return Promise.reject('Access Denied: Unable to get auth token');
    }
    const authToken = authResponse.authToken;
    if (!authToken) {
      return Promise.reject('Auth token is missing in response');
    }
    return authToken;
  } catch (error) {
    console.error('Error in exchangeToken:', error);
    return Promise.reject('An error occurred while exchanging token');
  }
};

export const getFloorplannerData = async (fpId: number | string, authToken?: string): Promise<any> => {
  try {
    const floorplannerData = await apiService.getRequest(`floorplanner/plan?fpId=${fpId}`, authToken);

    if (!floorplannerData || floorplannerData.error) {
      return Promise.reject('Error fetching floorplanner data');
    }
    return floorplannerData;
  } catch (error) {
    console.error('Error in getFloorplannerData:', error);
    return Promise.reject('An error occurred while fetching floorplanner data');
  }
};

export const fetchPlanData = async (
  data: string,
  building: IBuildingProviderContext,
  authToken?: string,
): Promise<{ plan: PlanModel; authToken: string; floorplannerData: any }> => {
  const { plan } = await decryptPlanData(data, building, authToken);
  const activeAuthToken = (await getValidKeycloakAccessTokenOrFallback(authToken)) || '';

  if (!plan.id || plan.id === -1) {
    return { plan, authToken: activeAuthToken, floorplannerData: {} };
  }

  const floorplannerData = await getFloorplannerData(plan.id, activeAuthToken);

  return { plan, authToken: activeAuthToken, floorplannerData };
};

export const retrievePanoMeasurements = async (
  plan: PlanModel,
  authToken: string,
): Promise<{
  cameraHeights: number[];
  defaultCameraHeights: number[];
  measurements: any[];
  panoDescriptions: string[];
  titlePictures: string[];
  compasses: number[];
  versions: number[];
  regions: string[];
}> => {
  let resourceURL = '';
  if (plan.eventId === 0 || plan.eventId === -1) {
    resourceURL = `${API_URL}floorplanner/measurements?koje_id=${plan.kojeId}&level=${plan.kojeLevel}`;
  } else {
    resourceURL = `${API_URL}floorplanner/measurementsEvent?event_id=${plan.eventId}`;
  }

  try {
    const activeAuthToken = await getValidKeycloakAccessTokenOrFallback(authToken);

    if (!activeAuthToken) {
      throw new Error('No valid authentication token available.');
    }
    const response = await fetch(resourceURL, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${activeAuthToken}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error: ${response.status}`);
    }

    const rawText = await response.text();
    const cameraHeights: number[] = [];
    const defaultCameraHeights: number[] = [];
    const measurements: any[] = [];
    const panoDescriptions: string[] = [];
    const titlePictures: string[] = [];
    const compasses: number[] = [];
    const versions: number[] = [];
    const regions: string[] = [];

    let parsedData: any[] = [];

    try {
      const json = JSON.parse(rawText);
      parsedData = Array.isArray(json) ? json : [json];
    } catch (e) {
      rawText.split(';').forEach((seg) => {
        const trimmed = seg.trim();
        if (trimmed !== '') {
          try {
            parsedData.push(JSON.parse(trimmed));
          } catch (err) {
            console.error('Error parsing legacy segment:', err);
          }
        }
      });
    }

    parsedData.forEach((item) => {
      const versionValue = 'version' in item ? parseFloat(item.version) : -1;
      const isNewVersion = versionValue !== -1;
      const regionValue = item.region || ERegionCode.DE;

      // lets check if the measurement actually has geometric data ---
      let isValid = false;
      let newScene: PvSceneExport | null = null;
      let oldPositions: any[] = [];

      if (isNewVersion) {
        newScene = item as PvSceneExport;
        // a new measurement is valid if it has at least one space mapped out
        if (newScene.measurements?.spaces && newScene.measurements.spaces.length > 0) {
          isValid = true;
        }
      } else {
        // an old measurement is valid if it has a Type 2 layout object with positions
        const targetArray = item.measurements || item.measurementContainer?.measurements;
        if (Array.isArray(targetArray)) {
          const layoutObj = targetArray.find((m: any) => (m.type === 2 || m.type === '2') && m.positions);
          if (layoutObj && layoutObj.positions && layoutObj.positions.length > 0) {
            oldPositions = layoutObj.positions;
            isValid = true;
          }
        }
      }

      if (isValid) {
        panoDescriptions.push(item.description ?? '');
        titlePictures.push(item.titlePicture ?? '');
        compasses.push(parseFloat(item.compass) || 0);
        versions.push(versionValue);
        regions.push(regionValue);

        if (isNewVersion && newScene) {
          // --- NEW VERSION (PvSceneExport) ---
          const defHeight = newScene.defaultCameraStandHeight ?? 0;
          defaultCameraHeights.push(defHeight);
          const cHeight = item.cameraHeight ?? newScene.measurements?.cameraHeight ?? defHeight;
          cameraHeights.push(cHeight);
          measurements.push(newScene);
        } else {
          // --- OLD VERSION (Legacy) ---
          const cHeight = item.cameraHeight ?? item.measurementContainer?.cameraHeight ?? 0;
          cameraHeights.push(cHeight);
          defaultCameraHeights.push(item.defaultCameraStandHeight ?? cHeight);
          measurements.push(oldPositions);
        }
      }
    });

    return {
      cameraHeights,
      defaultCameraHeights,
      measurements,
      panoDescriptions,
      titlePictures,
      compasses,
      versions,
      regions,
    };
  } catch (error) {
    console.error('Error retrieving pano measurements:', error);
    throw error;
  }
};
